import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData
} from "@tanstack/react-query";
import { QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE } from "api/landlord/property-groups";
import { useAuthContext } from "providers/Auth";
import { phoneNumbersApi } from "./api";
import { phoneNumberKeys } from "./queryKeys";
import {
  fetchLeasingAgentConfigs,
  LEASING_AGENT_CONFIGS_KEY
} from "views/settings-users-add-agent/lib/api";
import type {
  AssignedPropertiesPayload,
  BuyPhoneNumberPayload,
  CallsGoToFilter,
  ImportFromTwilioPayload,
  SearchAvailableParams,
  UpdateCallsGoToPayload,
  UpdatePhoneNumberPayload
} from "./types";

interface UsePhoneNumbersOptions {
  page?: number;
  perPage?: number;
  search?: string;
  enabled?: boolean;
  propertyIds?: number[];
  /**
   * CRM-1356: per-number ring counts for the Phone Numbers screen. The API resolves
   * the ring list of every voice number for it, so lookups that only want names —
   * the calls inbox's `usePartnerPhoneNames` — leave it off.
   */
  withInboundRecipients?: boolean;
  withRoutingSummary?: boolean;
  callsGoTo?: Exclude<CallsGoToFilter, "all">;
}

export const usePhoneNumbers = ({
  page = 1,
  perPage,
  search,
  enabled = true,
  propertyIds,
  withInboundRecipients = false,
  withRoutingSummary = false,
  callsGoTo
}: UsePhoneNumbersOptions = {}) =>
  useQuery({
    queryKey: [
      ...phoneNumberKeys.lists(),
      page,
      perPage,
      search ?? "",
      propertyIds ?? [],
      withInboundRecipients,
      withRoutingSummary,
      callsGoTo ?? "all"
    ],
    queryFn: () =>
      phoneNumbersApi.list(page, perPage, search, propertyIds, undefined, {
        withInboundRecipients,
        withRoutingSummary,
        callsGoTo
      }),
    enabled,
    placeholderData: keepPreviousData
  });

export const useAllPhoneNumbers = ({
  enabled = true,
  propertyIds
}: { enabled?: boolean; propertyIds?: number[] } = {}) =>
  useQuery({
    queryKey: [...phoneNumberKeys.lists(), "ALL", propertyIds ?? []],
    queryFn: () => phoneNumbersApi.listAll({ propertyIds }),
    enabled,
    placeholderData: keepPreviousData
  });

export const useSearchAvailableNumbers = (
  params: SearchAvailableParams,
  enabled: boolean
) =>
  useQuery({
    queryKey: phoneNumberKeys.searchAvailable(params),
    queryFn: () => phoneNumbersApi.searchAvailable(params),
    enabled,
    placeholderData: keepPreviousData
  });

export const useBuyPhoneNumber = () => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CREATE"],
    mutationFn: (payload: BuyPhoneNumberPayload) =>
      phoneNumbersApi.buy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: [QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE]
      });
      // The max_provisioned_phones lock reads the partner entity's unfiltered
      // count, so it has to be re-fetched whenever the count changes (CRM-759).
      refreshPartnerData();
    }
  });
};

export const useImportFromTwilio = () => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CREATE"],
    mutationFn: (payload: ImportFromTwilioPayload) =>
      phoneNumbersApi.importFromTwilio(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
      refreshPartnerData();
    }
  });
};

export const useUpdatePhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "UPDATE"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: UpdatePhoneNumberPayload;
    }) => phoneNumbersApi.update(commId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useUpdateCallsGoTo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CALLS_GO_TO", "UPDATE"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: UpdateCallsGoToPayload;
    }) => phoneNumbersApi.updateCallsGoTo(commId, payload),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: LEASING_AGENT_CONFIGS_KEY })
      ])
  });
};

export const useLeasingAgentConfigs = (enabled: boolean) =>
  useQuery({
    queryKey: LEASING_AGENT_CONFIGS_KEY,
    queryFn: fetchLeasingAgentConfigs,
    enabled,
    retry: false
  });

export const useDeletePhoneNumber = () => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "DELETE"],
    mutationFn: ({
      commId,
      newDefaultPhoneNumberId
    }: {
      commId: string;
      newDefaultPhoneNumberId?: string;
    }) => phoneNumbersApi.remove(commId, newDefaultPhoneNumberId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE]
        })
      ]);
      // Freeing a number has to unlock the buy/import affordance right away.
      refreshPartnerData();
    }
  });
};

export const usePropertyPhoneDefaults = (enabled: boolean = true) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefaults(),
    queryFn: () => phoneNumbersApi.listPropertyDefaults(),
    enabled
  });

export const usePartnerUsers = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["PHONE_NUMBERS", "PARTNER_USERS"],
    queryFn: phoneNumbersApi.listUsers,
    enabled
  });

export const usePropertyPhoneDefault = (
  propertyId: number,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefault(propertyId),
    queryFn: () => phoneNumbersApi.getPropertyDefault(propertyId),
    enabled
  });

export const useSetPropertyPhoneDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      propertyId,
      phoneNumberId
    }: {
      propertyId: number;
      phoneNumberId: string;
    }) => phoneNumbersApi.setPropertyDefault(propertyId, phoneNumberId),
    onSuccess: (_data, { propertyId }) => {
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefaults()
      });
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefault(propertyId)
      });
    }
  });
};

export const useUpdateAssignedProperties = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "UPDATE_PROPERTIES"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: AssignedPropertiesPayload;
    }) => phoneNumbersApi.updateAssignedProperties(commId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE]
        })
      ]);
    }
  });
};
